import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LabelPreviewBackgroundPickerComponent } from '../common/label-preview-background-picker/label-preview-background-picker.component';
import { LabelPreviewBackgroundId } from '../common/label-preview-backgrounds';
import { BaseComponent } from '../common/base.component';
import { ItemLibraryService } from '../services/item-library.service';
import {
  RANDOM_LOOT_DEFAULT_SIZE,
  RANDOM_LOOT_MAX_SIZE,
  RandomLootService,
} from '../services/random-loot.service';
import { FilterTitleComponent } from '../common/filter-title/filter-title.component';
import { DropPreviewComponent } from '../common/drop-preview/drop-preview.component';
import { DropPreviewService } from '../services/drop-preview.service';
import { DropPreviewSection } from '../services/drop-preview.models';

@Component({
  selector: 'app-preview-tab',
  imports: [
    DropPreviewComponent,
    LabelPreviewBackgroundPickerComponent,
    FilterTitleComponent,
    FormsModule,
  ],
  templateUrl: './preview-tab.component.html',
  styleUrl: './preview-tab.component.scss',
})
export class PreviewTabComponent extends BaseComponent implements OnInit {
  readonly dropPreviewService = inject(DropPreviewService);
  readonly itemLibraryService = inject(ItemLibraryService);
  readonly randomLootService = inject(RandomLootService);

  readonly randomLootDefaultSize = RANDOM_LOOT_DEFAULT_SIZE;
  readonly randomLootMaxSize = RANDOM_LOOT_MAX_SIZE;

  randomLootAreaLevelInput: number | '' = '';
  randomLootSizeInput = RANDOM_LOOT_DEFAULT_SIZE;
  randomLootIncludeIdentified = false;

  ngOnInit(): void {
    this.syncRandomLootInputsFromSettings();
  }

  toggleSection(section: DropPreviewSection): void {
    this.dropPreviewService.toggleSection(section);
  }

  toggleRandomLoot(): void {
    this.dropPreviewService.toggleRandomLoot();
  }

  toggleAllDropTierSections(): void {
    this.dropPreviewService.toggleAllDropTierSections();
  }

  toggleAllCustomRuleSections(): void {
    this.dropPreviewService.toggleAllCustomRuleSections();
  }

  clearItemLibrary(): void {
    this.itemLibraryService.clearAll();
  }

  dismissItemLibraryExplainer(): void {
    this.settings.dismissItemLibraryExplainer();
  }

  onRandomLootAreaLevelChange(): void {
    const raw = this.randomLootAreaLevelInput;
    if (raw === '' || raw === null || raw === undefined) {
      this.randomLootService.setAreaLevel(null);
      this.dropPreviewService.setAreaLevel(null);
      this.randomLootSizeInput = this.randomLootService.settings().size;
      return;
    }

    const parsed = Math.round(Number(raw));
    if (!Number.isFinite(parsed)) {
      this.syncRandomLootInputsFromSettings();
      return;
    }

    const areaLevel = Math.min(100, Math.max(1, parsed));
    this.randomLootService.setAreaLevel(areaLevel);
    this.dropPreviewService.setAreaLevel(areaLevel);
    this.randomLootAreaLevelInput = areaLevel;
    this.randomLootSizeInput = this.randomLootService.settings().size;
  }

  onRandomLootSizeChange(): void {
    if (this.randomLootService.sizeInputDisabled()) {
      this.randomLootSizeInput = this.randomLootService.settings().size;
      return;
    }

    const parsed = Math.round(Number(this.randomLootSizeInput));
    if (!Number.isFinite(parsed)) {
      this.randomLootSizeInput = this.randomLootService.settings().size;
      return;
    }

    this.randomLootService.setSize(parsed);
    this.randomLootSizeInput = this.randomLootService.settings().size;
  }

  async generateLootPile(): Promise<void> {
    this.dropPreviewService.ensureSectionEnabled('randomLoot');
    await this.randomLootService.generateLootPile();
  }

  toggleRandomLootIncludeIdentified(): void {
    this.randomLootIncludeIdentified = !this.randomLootIncludeIdentified;
    this.randomLootService.setIncludeIdentified(this.randomLootIncludeIdentified);
  }

  onPreviewBgSelect(id: LabelPreviewBackgroundId): void {
    this.settings.setLabelPreviewBg(id);
  }

  private syncRandomLootInputsFromSettings(): void {
    const { areaLevel, size, includeIdentified } = this.randomLootService.settings();
    this.randomLootAreaLevelInput = areaLevel ?? '';
    this.dropPreviewService.setAreaLevel(areaLevel);
    this.randomLootSizeInput = size;
    this.randomLootIncludeIdentified = includeIdentified;
  }
}
